import { useEffect, useRef, useCallback } from 'react';
import { useExportNotificationContext } from 'contexts/ExportNotificationContext';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import NotificationItem from './NotificationItem';

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onMarkAsSeen?: () => void;
}

const NotificationPanel = ({ isOpen, onClose, onMarkAsSeen }: NotificationPanelProps) => {
  const { exports, isLoading, isLoadingMore, hasNextPage, totalItems, loadMoreExports, handleDownload, handleDelete } =
    useExportNotificationContext();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  const hasMarkedAsSeen = useRef(false);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !observerTarget.current) return;

    const container = scrollContainerRef.current;
    const target = observerTarget.current;
    const targetPosition = target.getBoundingClientRect();
    const containerPosition = container.getBoundingClientRect();

    if (targetPosition.top <= containerPosition.bottom && hasNextPage && !isLoadingMore && !isLoading) {
      loadMoreExports();
    }
  }, [hasNextPage, isLoadingMore, isLoading, loadMoreExports]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (onMarkAsSeen && exports.length > 0 && !hasMarkedAsSeen.current) {
        hasMarkedAsSeen.current = true;
        onMarkAsSeen();
      }
    } else {
      document.body.style.overflow = '';
      hasMarkedAsSeen.current = false;
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, onMarkAsSeen, exports.length]);

  useEffect(() => {
    if (isOpen && hasNextPage && !isLoading && !isLoadingMore && exports.length < totalItems) {
      const timer = setTimeout(() => {
        loadMoreExports();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, hasNextPage, isLoading, isLoadingMore, exports.length, totalItems, loadMoreExports]);

  const pendingCount = exports.filter(exp => exp.status === 'queued' || exp.status === 'processing').length;

  // Don't render anything if panel is closed
  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} />

      <div
        className="fixed top-0 right-0 h-full w-full sm:w-96 bg-surface border-l border-border z-50
          transform transition-transform duration-300 ease-in-out shadow-xl translate-x-0"
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-border bg-surface sticky top-0 z-10">
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Export History</h2>
              <p className="text-xs text-text-secondary mt-0.5">
                {totalItems} {totalItems === 1 ? 'item' : 'items'}
                {pendingCount > 0 && ` · ${pendingCount} processing`}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="p-2">
              <Icon name="X" size={20} />
            </Button>
          </div>

          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
            {isLoading && exports.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 space-y-3">
                <Icon name="Loader" size={32} className="animate-spin text-primary" />
                <p className="text-sm text-text-secondary">Loading export history...</p>
              </div>
            ) : exports.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 space-y-3 px-6">
                <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center">
                  <Icon name="FileText" size={28} className="text-neutral-400" />
                </div>
                <div className="text-center">
                  <h3 className="text-base font-medium text-text-primary mb-1">No exports yet</h3>
                  <p className="text-sm text-text-secondary">Your export history will appear here</p>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {exports.map(item => (
                  <NotificationItem key={item.jobId} item={item} onDownload={handleDownload} onDelete={handleDelete} />
                ))}

                {hasNextPage && (
                  <div ref={observerTarget} className="p-4 flex items-center justify-center">
                    {isLoadingMore && (
                      <div className="flex items-center space-x-2 text-text-secondary">
                        <Icon name="Loader" size={16} className="animate-spin" />
                        <span className="text-sm">Loading more...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationPanel;
